import { customerAgencyNameOf } from '@featit/shared/customer-sender';
import { navigation } from '@featit/shared/i18n/messages/en/navigation';
import type { TFn } from '@/i18n/LocaleProvider';

/** The selector's own words, by catalogue key. */
type SelectorWordKeyType =
  | 'navigation.sidebar.allWorkAreas'
  | `navigation.selector.${keyof typeof navigation.selector}`;
type SelectorWordsType = (key: SelectorWordKeyType) => string;

function englishSelectorWord(key: SelectorWordKeyType): string {
  if (key === 'navigation.sidebar.allWorkAreas') return navigation.sidebar.allWorkAreas;
  return navigation.selector[
    key.slice('navigation.selector.'.length) as keyof typeof navigation.selector
  ];
}

export interface WorkAreaSelectorState {
  /** The overview shows every work area at once, so nothing is "selected". */
  overviewMode: boolean;
  /**
   * The namespace the app is scoped to (F-0383), when it is — named by the switcher the way the
   * page's own scope banner names it, never the work area last visited behind it (I-1089).
   */
  namespaceName?: string;
  selectedName?: string;
  /**
   * F-0739 (PJ-8, I-2232): the Public work area the page reads, when the reader is not a member of
   * it: the page is in it, so the selector says so rather than "No work area selected".
   */
  viewedPublicName?: string;
  /** A discovery has succeeded at least once, so the store knows what exists. */
  hasAnswer: boolean;
  /** A discovery is in flight. On its own this says nothing about what exists. */
  loading: boolean;
  optionCount: number;
}

/**
 * PX-33 (I-2384, point 3): what the switcher calls a customer guest's work area: the agency, by the
 * display name of the organisation it lies in (`customerAgencyNameOf`, as the reply names it), never
 * the work area's own name, which the agency chose for itself. Null where there is no agency to name
 * (a person's own namespace, or none named): the caller then says whose pages they are.
 */
export function guestScopeName(
  workArea: { namespaceName?: string | null; namespaceIsOwn?: boolean } | undefined,
): string | null {
  return customerAgencyNameOf({
    organisation: workArea && !workArea.namespaceIsOwn ? workArea.namespaceName : null,
  });
}

/** What the phone's scope line knows when it decides whether to draw a name yet (I-1423). */
export interface ScopeNameState {
  overviewMode: boolean;
  namespaceName?: string;
  /** The address names a work area the store holds. */
  hasWorkArea: boolean;
  /** The name this browser last saw at this very address. */
  hasRememberedName: boolean;
  hasAnswer: boolean;
  loading: boolean;
  /** A discovery failed and the store holds the failure. */
  failed: boolean;
}

/**
 * Whether the phone's scope line holds its place without a name or a ▾ yet, so the name and ▾ arrive
 * together where they stay (I-1423).
 *
 * Nothing names the work area, and the work areas have not answered: loading, or about to be.
 * `loading` is false until `fetchProjects` runs in an effect, and those frames are no failure, so
 * they are waiting too. Only a held failure ends the wait: the line then says "Unavailable ▾".
 */
export function scopeNameIsPending(state: ScopeNameState): boolean {
  if (state.overviewMode || state.namespaceName || state.hasWorkArea || state.hasRememberedName) {
    return false;
  }
  if (state.hasAnswer) return false;
  return state.loading || !state.failed;
}

/**
 * I-2503: the longest a guest's scope line holds its name for the agency's logo. A mark that never
 * settles (its chunk is offline) then lets the name and ▾ be drawn without it, rather than never.
 */
export const SCOPE_MARK_HOLD_MAX_MS = 1500;

/** What the phone's scope line knows about the agency logo drawn in front of a guest's name. */
export interface ScopeMarkState {
  /** The row draws a guest's work area, so a logo may come before its name. */
  guest: boolean;
  /**
   * Before the work areas answer: the name remembered at this address had the agency's logo in
   * front of it, so the logo will come again (I-2503, a returning guest).
   */
  rememberedMark: boolean;
  /**
   * The work area the hold is for, by the address's reference: the same before the answer and
   * after it, so a returning guest's hold and its give-up carry across the answer.
   */
  holdFor: string | null;
  /** The work area whose mark has settled: its picture is drawn, or it is known to have none. */
  settledFor: string | null;
  /** The work area the hold gave up on after `SCOPE_MARK_HOLD_MAX_MS`. */
  gaveUpFor: string | null;
}

/**
 * Whether a guest's scope line keeps its name and ▾ invisible, in their room, for the agency logo
 * (I-2503, I-1423). The logo is drawn in front of the name and arrives after the work areas answer
 * (its chunk, the namespace tree, then its picture), so a name drawn before it was pushed right by
 * the logo's width. Until the mark settles, nothing that the logo would move is drawn. A returning
 * guest whose remembered name had the logo is held from the first frame, before the answer.
 */
export function scopeLineHeldForMark(state: ScopeMarkState): boolean {
  if (!(state.guest || state.rememberedMark) || !state.holdFor) return false;
  return state.settledFor !== state.holdFor && state.gaveUpFor !== state.holdFor;
}

/**
 * What the work-area selector reads, given what the store actually knows.
 *
 * The rule the old expression broke is the one `ProjectStore.fetchProjects` already states for the
 * failure path: a fetch that has not settled is not a statement about how many work areas exist. It
 * read `loading ? 'Loading work areas' : 'No work area'`, and `loading` goes true on every refetch,
 * not just the first. On a desktop install with no reachable cloud service those refetches are
 * near-continuous, so the selector said "Loading work areas" beside a pane that had already
 * resolved to "No work area selected" — two parts of one screen disagreeing about a settled fact.
 *
 * Once the store has an answer it keeps it, and the selector reads from the answer. `loading` only
 * decides anything before the first one arrives.
 */
export function workAreaSelectorLabel(
  state: WorkAreaSelectorState,
  /** The reader's catalogue (`useT()`); English where nothing is drawn (a test, a guard). */
  t: SelectorWordsType = englishSelectorWord,
): string {
  if (state.overviewMode) return t('navigation.sidebar.allWorkAreas');
  if (state.namespaceName) return state.namespaceName;
  if (state.selectedName) return state.selectedName;
  if (state.viewedPublicName) return state.viewedPublicName;
  if (!state.hasAnswer) {
    // Settled with no answer means the discovery failed. Saying "No work areas" there would report
    // an outage as an empty account, which is the lie the store's error path exists to avoid.
    return state.loading ? t('navigation.selector.loading') : t('navigation.selector.unavailable');
  }
  // The empty case gets its own words. "No work area" was the same label as "you have some and
  // picked none", so the one screen that most needs a next step read like an ordinary idle state.
  if (state.optionCount === 0) return t('navigation.selector.noneYet');
  return t('navigation.selector.noneSelected');
}

/**
 * Whether the selector is dead to a click.
 *
 * A refetch used to disable it, which on desktop meant the reader could not open the switcher most
 * of the time. Nothing about a background refresh makes the work areas already listed unpickable.
 *
 * An empty account is not a dead control either. The menu carries *New work area* under the list,
 * so on the one screen where a reader has nothing and most needs a next step, the control that
 * says "No work areas yet" is the control that opens the way out of it. Only a store that has no
 * answer at all — a first discovery still in flight, or one that failed — has nothing to offer.
 */
export function workAreaSelectorDisabled(state: WorkAreaSelectorState): boolean {
  return !state.hasAnswer && state.optionCount === 0;
}

/**
 * Where to tell someone the work-area selector is, in the shell they are actually looking at.
 *
 * Four empty states said "the header". The desktop shell puts the selector in the sidebar, so on
 * the one build where a reader is most likely to have no work area yet, the instruction pointed at
 * the wrong edge of the window.
 *
 * ⛔ **And so does the browser shell** (I-0450). `<ProjectSelector>` is rendered in exactly one
 * place — the sidebar, in `Layout` — for both runtimes; the narrow top bar shows the work area's
 * *name* and no control at all. Saying "the header" on the web sent six empty states to an edge of
 * the window that has never held the selector. The runtime does not change where it is, so this no
 * longer asks.
 */
export function workAreaSelectorLocation(): string {
  return 'the sidebar';
}

/** `workAreaSelectorLocation` in the reader's language, for a sentence that says "in {location}". */
export function workAreaSelectorLocationIn(t: TFn): string {
  return t('sidebar.selectorLocation');
}
